import { NestedValue } from 'react-hook-form';

import {
  compact,
  filter,
  find,
  flattenDeep,
  get,
  isEmpty,
  join,
  map,
  merge,
  random,
  reduce,
  replace,
  set,
  some as _some,
  split,
} from 'lodash';
import { TPatientFormTemplate } from 'ui-model';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { Language } from '@lib/resources/types';

import { INDICATOR_FIELDS, PATIENT_FORM_VERSION_REDESIGN_POSTFIX, PATIENT_FORM_VERSIONS } from './constants';
import {
  EChecklistItemCategory,
  EInsuranceCaptureMethod,
  FormQuestionTypeKeys,
  TCustomFormValues,
  TFormAnswerValues,
  TFormBuilderState,
  TFormQuestionValues,
  TFormSubQuestionValues,
  THistoryQuestionValues,
  TInsuranceCardOptions,
  TList,
  TQuestionTypeConfigurationOptions,
} from './types';

// ENUMS ------------------------------------------------------------------------------------------------------
export enum FormTypeKeys {
  intake = 'intake',
  feedback = 'feedback',
  preQualification = 'pre-qualification',
  ccda = 'ccda',
  schedulerAssist = 'scheduler-assist',
  queueIntake = 'queue-intake',
}

export enum FormSourceKeys {
  lumaHealth = 'luma',
  surveyMonkey = 'surveymonkey',
}

export enum FormAllRequiredQuestionsKeys {
  all = 'all',
  none = 'none',
  some = 'some',
}

export enum RedirectConfigurationTypeKeys {
  final = 'isFinal',
  goTo = 'goTo',
  goToForm = 'goToForm',
  none = 'none',
  scheduler = 'scheduler',
  url = 'externalUrl',
  joinQueue = 'joinQueue',
}
export enum DuplicateQuestionTypeKeys {
  last = 'last',
  first = 'first',
  custom = 'custom',
}

export enum MoveQuestionTypeKeys {
  last = 'last',
  first = 'first',
  custom = 'custom',
}

export enum DetailsPanelKeys {
  form = 'form',
  question = 'question',
}

export enum FormQuestionVisibilityKeys {
  default = 'default',
  hidden = 'hidden',
  visible = 'visible',
}

export enum PageStatus {
  error = 'error',
  idle = 'idle',
  loading = 'loading',
  ready = 'ready',
}

// CONSTANTS --------------------------------------------------------------------------------------------------
export const formBuilderInitialState: TFormBuilderState = { status: PageStatus.idle, data: false, widgetUser: '' };
export const questionTypesConfiguration: Record<FormQuestionTypeKeys, Partial<TQuestionTypeConfigurationOptions>> = {
  multiple_choice: {
    canSelectAnswers: true,
    icon: 'check_box_outlined',
    isAvailableOnMenu: true,
    label: 'Multiple Choice',
    showImportAnswersButton: true,
    showPreviewButton: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
    titlePlaceholder: 'Type question here...',
  },
  yes_no: {
    canSelectAnswers: true,
    icon: 'tonality',
    label: 'Yes/No',
    showImportAnswersButton: true,
    showPreviewButton: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
    titlePlaceholder: 'Type question here...',
  },
  dropdown: {
    canSelectAnswers: true,
    icon: 'keyboard_arrow_down',
    isAvailableOnMenu: true,
    label: 'Dropdown',
    showImportAnswersButton: true,
    showPreviewButton: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
    titlePlaceholder: 'Type question here...',
  },
  short_text: {
    icon: 'short_text',
    isAvailableOnMenu: true,
    isText: true,
    label: 'Short Text',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  long_text: {
    icon: 'notes',
    isAvailableOnMenu: true,
    isText: true,
    label: 'Long Text',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  email: {
    icon: 'email',
    isAvailableOnMenu: true,
    isText: true,
    label: 'Email',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  phone_number: {
    icon: 'phone',
    isAvailableOnMenu: true,
    isText: true,
    label: 'Phone Number',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  resources: {
    label: 'Resources',
    icon: 'business',
    isAvailableOnMenu: true,
    disabled: false,
    titlePlaceholder: 'Select and search the resources...',
    showSettingsButton: true,
    showRequiredButton: true,
    showRedirectContent: true,
    isFinal: false,
    isText: false,
    supportsSquiggly: true,
  },
  date: {
    icon: 'today',
    isAvailableOnMenu: true,
    isText: true,
    label: 'Date',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  address: {
    icon: 'my_location',
    isAvailableOnMenu: true,
    label: 'Address',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  ssn: {
    icon: 'assignment_ind',
    isAvailableOnMenu: true,
    isText: true,
    label: 'SSN',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  zipcode: {
    icon: 'my_location',
    isAvailableOnMenu: true,
    isText: true,
    label: 'Zip Code',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
  },
  medication_search: {
    icon: 'medication',
    isAvailableOnMenu: true,
    isLumaIcon: true,
    label: 'Medication search',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    titlePlaceholder: 'What medications are you taking?',
  },
  pharmacy_search: {
    icon: 'local_pharmacy',
    isAvailableOnMenu: true,
    label: 'Pharmacy search',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    titlePlaceholder: 'What is your pharmacy?',
  },
  e_signature: {
    icon: 'edit',
    isAvailableOnMenu: true,
    label: 'E-Signature',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
  },
  template: {
    canBeFinal: true,
    icon: 'code',
    isAvailableOnMenu: true,
    isFinal: true,
    label: 'Template',
    showPreviewButton: true,
    showRedirectContent: true,
    showSettingsButton: true,
    showSquigglyButton: true,
  },
  '2FA': {
    icon: 'lock',
    label: 'Two-factor authentication',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: false,
    showSquigglyButton: true,
  },
  patient_information: {
    icon: 'person',
    isAvailableOnMenu: true,
    label: 'Patient Information',
    showPreviewButton: true,
    showRedirectContent: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    titlePlaceholder: 'Patient Information',
  },
  scheduler: {
    icon: 'insert_invitation',
    isAvailableOnMenu: true,
    label: 'Web scheduler',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    titlePlaceholder: 'Web scheduler',
  },
  group: {
    icon: 'list',
    isAvailableOnMenu: true,
    label: 'Question Group',
    showPreviewButton: true,
    showRedirectContent: true,
    showSettingsButton: true,
    showSquigglyButton: true,
  },
  jump: {
    canSelectAnswers: true,
    icon: 'low_priority',
    isAvailableOnMenu: true,
    label: 'Jump',
    showScript: true,
    titlePlaceholder: 'Jump',
  },
  imh: {
    icon: 'description',
    isAvailableOnMenu: true,
    label: 'IMH',
    titlePlaceholder: 'IMH',
    showCanSkip: true,
  },
  insurance_capture: {
    icon: 'insurance_capture',
    isAvailableOnMenu: true,
    isLumaIcon: true,
    label: 'Insurance Capture',
    showSettingsButton: true,
    canSelectAnswers: true,
    showPreviewButton: true,
    titlePlaceholder: 'Insurance Capture',
  },
  information_capture: {
    icon: 'fullscreen',
    isAvailableOnMenu: true,
    label: 'Information Capture',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
  },
  insurance_card: {
    icon: 'fullscreen',
    isAvailableOnMenu: true,
    label: 'Insurance Card',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
  },
  copay: {
    canBeFinal: true,
    icon: 'monetization_on',
    isAvailableOnMenu: true,
    label: 'Payment',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    showSquigglyButton: true,
    supportsSquiggly: true,
    titlePlaceholder: 'Your copay for this appointment is:',
  },
  docusign_templates: {
    canBeFinal: true,
    icon: 'docusign',
    isAvailableOnMenu: true,
    isLumaIcon: true,
    label: 'DocuSign',
    showDescriptionField: true,
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    isText: false,
  },
  medication_list: {
    icon: 'medication_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Medication list',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Please list any medications you’re currently taking',
  },
  immunization_history: {
    icon: 'immunization_history_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Immunization History',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Immunization History',
  },
  allergy: {
    icon: 'allergies_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Allergies',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Please search for your allergy',
  },
  surgical_history: {
    icon: 'surgical_history_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Surgical History',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Surgical History',
  },
  family_history: {
    icon: 'family_history_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Family History',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Family History',
  },
  social_history: {
    icon: 'social_history_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Social History',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Social History',
  },
  demographics: {
    icon: 'demographics_ccda',
    isAvailableOnMenu: true,
    isCCDA: true,
    isLumaIcon: true,
    label: 'Demographics',
    showPreviewButton: true,
    showRedirectContent: true,
    showRequiredButton: true,
    showSettingsButton: true,
    titlePlaceholder: 'Demographics',
  },
};

// GENERAL FUNCTIONS ------------------------------------------------------------------------------------------
export const getJumpLogicBoundQuestionsNames = (
  deletedQuestionIndex: number,
  questions: TFormQuestionValues[],
): string[] => {
  const deletedQuestion = questions[deletedQuestionIndex];
  const boundQuestionsNames: string[] = [];

  if (deletedQuestion) {
    map(questions, (question: TFormQuestionValues, questionIndex: number) => {
      const questionName = `questions[${questionIndex}]`;

      if (question.redirectValue === deletedQuestion.id) {
        boundQuestionsNames.push(questionName);
      }

      map(question.answers, (answer: TFormAnswerValues, answerIndex: number) => {
        if (answer.redirectValue === deletedQuestion.id) {
          boundQuestionsNames.push(`${questionName}.answers[${answerIndex}]`);
        }
      });

      map(question.subQuestions, (subQuestion: TFormSubQuestionValues, subQuestionIndex: number) => {
        const subQuestionName = `${questionName}.subQuestions[${subQuestionIndex}]`;

        if (subQuestion.redirectValue === deletedQuestion.id) {
          boundQuestionsNames.push(subQuestionName);
        }

        map(subQuestion.answers, (answer: TFormAnswerValues, answerIndex: number) => {
          if (answer.redirectValue === deletedQuestion.id) {
            boundQuestionsNames.push(`${subQuestionName}.answers[${answerIndex}]`);
          }
        });
      });
    });
  }

  return boundQuestionsNames;
};

export const generateRandomIdWithPrefix = (prefix: string) =>
  `${prefix ? `${prefix}-` : ''}${new Date().getTime()}${random(99999)}`;

export const getQuestionItemConfiguration = (questionType: FormQuestionTypeKeys): TQuestionTypeConfigurationOptions => {
  const defaults = {
    canBeFinal: false,
    canSelectAnswers: false,
    disabled: false,
    icon: '',
    isAvailableOnMenu: false,
    isCCDA: false,
    isFinal: false,
    isLumaIcon: false,
    isText: false,
    label: '',
    showDescriptionField: false,
    showImportAnswersButton: false,
    showScript: false,
    showSettingsButton: false,
    supportsSquiggly: false,
    titlePlaceholder: 'Type title here...',
  };

  return merge({}, defaults, questionTypesConfiguration[questionType]) as TQuestionTypeConfigurationOptions;
};

export const getSquiggly = (question: TFormQuestionValues, fieldLocaleSufix: string): TList | TList[] =>
  question.subQuestions.length
    ? getSquigglies(question.subQuestions, fieldLocaleSufix)
    : {
        id: question.squiggly,
        name: get(question, `title${fieldLocaleSufix}`, 'title'),
      };

export const getSquigglies = (
  questions: TFormQuestionValues[] | TFormSubQuestionValues[],
  fieldLocaleSufix: string,
): TList[] => {
  const allQuestions = map(questions, (item: any) => getSquiggly(item, fieldLocaleSufix));

  return flattenDeep(allQuestions) as TList[];
};

export const getRedirectConfigurationText = (redirectType: string, value: string) => {
  switch (redirectType) {
    case RedirectConfigurationTypeKeys.final:
      return 'Final';
    case RedirectConfigurationTypeKeys.goTo:
      return `Jump to #${value}`;
    case RedirectConfigurationTypeKeys.goToForm:
      return 'Jump to external form';
    case RedirectConfigurationTypeKeys.scheduler:
      return 'Open web scheduler';
    case RedirectConfigurationTypeKeys.url:
      return 'Redirect to URL and complete';
    case RedirectConfigurationTypeKeys.joinQueue:
      return 'Join queue and complete';
    case RedirectConfigurationTypeKeys.none:
    default:
      return 'Go to next question';
  }
};

export const insuranceVerificationBranchingAnswers = [
  {
    english: 'If patient has at least one valid insurance on file',
    spanish: 'Si el paciente tiene al menos un seguro válido registrado',
    portuguese: 'Se o paciente tiver pelo menos um seguro válido registrado',
  },
  {
    english: 'If patient has no insurance on file, or insurance on file is invalid',
    spanish: 'Si el paciente no tiene seguro registrado o el seguro registrado no es válido',
    portuguese: 'Se o paciente não tiver seguro registrado ou se o seguro registrado for inválido',
  },
];
export const insuranceVerificationBranchingAnswersSchedulerAssistForms = [
  {
    english: 'If patient has at least one insurance',
    spanish: 'Si el paciente tiene al menos un seguro',
    portuguese: 'Se o paciente tiver pelo menos um seguro',
  },
  {
    english: 'If patient has no insurance',
    spanish: 'Si el paciente no tiene seguro',
    portuguese: 'Se o paciente não tiver seguro',
  },
];

// TODO rrigonia Insurance Capture branchingAnswers
export const insuranceCaptureBranchingAnswers = [
  {
    english: 'If patient has at least one insurance in EHR they confirmed',
    spanish: 'Si el paciente tiene al menos un seguro en EHR, lo confirmaron',
    portuguese: 'Se o paciente tiver pelo menos um seguro confirmado no EHR',
  },
  {
    english: 'If patient has no insurance in the EHR, or deletes ALL insurances presented from EHR, or is self-pay',
    spanish:
      'Si el paciente no tiene seguro en el EHR, o elimina TODOS los seguros presentados en el EHR, o paga por su cuenta',
    portuguese:
      'Se o paciente não tiver seguro no EHR, ou excluir TODOS os seguros apresentados no EHR, ou não tiver seguro',
  },
];

export const insuranceEligibilityBranchingAnswers = [
  {
    english: 'If eligibility result is unknown',
    spanish: 'Si se desconoce el resultado de elegibilidad',
    portuguese: 'Se o resultado de elegibilidade for desconhecido',
  },
  {
    english: 'If patient is eligible',
    spanish: 'Si el paciente es elegible',
    portuguese: 'Se o paciente for elegível',
  },
  {
    english: 'If patient is NOT eligible',
    spanish: 'Si el paciente NO es elegible',
    portuguese: 'Se o paciente NÃO for elegível',
  },
];

export const getQuestionsToCheck = (questions: TFormQuestionValues[] | TFormSubQuestionValues[]) => {
  const flattenedQuestions = reduce(
    questions,
    (acumulator: any, question: any) => {
      if (!question.subQuestions.length) {
        return [...acumulator, question];
      }

      return [...acumulator, ...question.subQuestions];
    },
    [],
  );

  return filter(flattenedQuestions, (question: TFormQuestionValues) => !/payment|template|group/.test(question.type));
};

export const hasSchedulerQuestion = (questions: TFormQuestionValues[], onlyOnDefer?: boolean) =>
  _some(questions, (item) => {
    if (onlyOnDefer) {
      return item.type === FormQuestionTypeKeys.scheduler && item.schedulerBookOnFormComplete;
    }
    return item.type === FormQuestionTypeKeys.scheduler;
  });

export const hasSchedulerRedirect = (questions: TFormQuestionValues[]): boolean => {
  let schedulerRedirectFound = false;

  reduce(
    questions,
    (acumulator: any, question: any) => {
      if (
        question.redirectType === RedirectConfigurationTypeKeys.scheduler ||
        !!filter(question.answers, { redirectType: RedirectConfigurationTypeKeys.scheduler }).length
      ) {
        schedulerRedirectFound = true;
      }

      if (!get(question, 'subQuestions', []).length) {
        return [...acumulator, question];
      }

      return [...acumulator, ...get(question, 'subQuestions', [])];
    },
    [],
  );

  return schedulerRedirectFound;
};

export function extractNumberFromTemplateVersion(text: string): number | null {
  const pattern = /^(\d+\.?\d*)/;

  const match = text.match(pattern);
  if (match) {
    return parseFloat(match[1]);
  }
  return null;
}

// extract the redirect configuration value from goTo, externalUrl and params fields to a single one
export const getRedirectConfigurationValue = (values: object): string | object => {
  // Now we save scheduler instead of reschedule, but we did not ran a migrate script.
  const redirectType: string = replace(
    get(values, 'redirectType', ''),
    'reschedule',
    RedirectConfigurationTypeKeys.scheduler,
  );

  if (redirectType === RedirectConfigurationTypeKeys.scheduler) {
    return get(values, 'params', get(values, 'properties.params', {})) as object;
  }

  if (redirectType === RedirectConfigurationTypeKeys.url) {
    return get(values, 'url', get(values, 'properties.url', get(values, 'externalUrl', ''))) as string;
  }

  if (redirectType === RedirectConfigurationTypeKeys.joinQueue) {
    return get(values, 'queue', get(values, 'properties.queue')) as string;
  }

  return get(values, redirectType, get(values, `properties.${redirectType}`, '')) as string;
};

// GET FORM COMPONENTS DEFAULT VALUES FUNCTIONS FOR NEW ITEMS -------------------------------------------------
export const getDefaultValuesCustomFormObject = (
  templateVersion: string = PATIENT_FORM_VERSIONS.stable,
): TCustomFormValues => {
  const questions = [
    getDefaultValuesQuestionObject(FormQuestionTypeKeys.multipleChoice, FormTypeKeys.intake),
  ] as NestedValue<TFormQuestionValues[]>;
  return {
    active: true,
    allRequired: getAllRequiredQuestionsFlag(questions),
    checklistCategory: EChecklistItemCategory.Intake,
    facilities: '',
    id: '',
    insurance: false,
    language: Language.English,
    questions,
    reminder: false,
    sendToDirectMessageEmail: false,
    writebackToEhr: false,
    writebackPDFToEhr: false,
    categoryNameToWritebackPDF: '',
    respectTemplateHTML: false,
    skipWelcomeQuestion: false,
    source: FormSourceKeys.lumaHealth,
    templateAction: 'Start',
    templateActionPortuguese: '',
    templateActionSpanish: '',
    templateId: generateRandomIdWithPrefix('welcome'),
    templateText: '',
    templateTextPortuguese: '',
    templateTextSpanish: '',
    templateTitle: 'Welcome!',
    templateTitlePortuguese: '',
    templateTitleSpanish: '',
    templateWrapLabels: false,
    title: '',
    type: FormTypeKeys.intake,
    version: templateVersion,
    questionsForStaffReview: {},
  };
};

export const getDefaultValuesAnswerObject = (): TFormAnswerValues => ({
  allowResourcesSearching: true,
  code: '',
  disabled: false,
  hideCalendar: false,
  id: generateRandomIdWithPrefix('answer'),
  label: '',
  labelPortuguese: '',
  labelSpanish: '',
  redirectType: RedirectConfigurationTypeKeys.none,
  redirectValue: '',
  queueSquigglyMapping: [],
  score: '',
  showFilters: false,
  showZipcodeFilter: false,
  useExternalResources: false,
  useFilterFromResourcesQuestions: false,
  zipcodeMaxDistance: -1,
});

export const getQuestionCustomInformation = (questionType: string, formType: string): Partial<TFormQuestionValues> => {
  if (questionType === FormQuestionTypeKeys.patientInformation) {
    const patientInformationQuestions = [
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'firstname',
        mappingField: 'firstname',
        mustExist: true,
        required: true,
        squiggly: 'patient.firstName',
        title: 'First Name',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'lastname',
        mappingField: 'lastname',
        mustExist: true,
        required: true,
        squiggly: 'patient.lastName',
        title: 'Last Name',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.phoneNumber, formType),
        id: 'phone',
        mappingField: 'contactPhone',
        mustExist: true,
        required: true,
        squiggly: 'patient.phone',
        title: 'Phone Number',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.email, formType),
        id: 'email',
        mappingField: 'contactEmail',
        squiggly: 'patient.email',
        title: 'E-mail',
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.date, formType),
        id: 'dateOfBirth',
        mappingField: 'dateOfBirth',
        mustExist: true,
        required: true,
        squiggly: 'patient.dob',
        title: 'Date of Birth',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.address, formType),
        id: 'address',
        mappingField: 'address',
        squiggly: 'patient.address',
        title: 'Address',
      },
    ];

    if (formType === FormTypeKeys.preQualification || formType === FormTypeKeys.queueIntake) {
      patientInformationQuestions.push({
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.twoFactor, formType),
        required: true,
        title:
          'To confirm your identity, we will send you a verification code. If you entered a mobile number, you will receive the code via text message. If you entered a landline number, you will receive the code via a phone call.',
      });
    }

    return {
      subQuestions: patientInformationQuestions as NestedValue<TFormSubQuestionValues[]>,
      visibility: FormQuestionVisibilityKeys.default,
    };
  }

  if (questionType === FormQuestionTypeKeys.group) {
    return {
      subQuestions: [getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType)] as NestedValue<
        TFormSubQuestionValues[]
      >,
      visibility: FormQuestionVisibilityKeys.default,
    };
  }
  if (questionType === FormQuestionTypeKeys.imh) {
    return {
      imhQuestionnaires: [{ id: uuid() }],
    };
  }
  if (questionType === FormQuestionTypeKeys.insuranceCapture) {
    return {
      canSkip: false,
      includeInsuranceCard: TInsuranceCardOptions.OPTIONAL,
      insuranceCaptureMethod: EInsuranceCaptureMethod.MANUAL,
      runInsuranceEligibility: false,
      onlyPullInsuranceEHR: false,
      writebackPDFToEhrOnlyWhenInsuranceIsAltered: false,
    };
  }
  if (questionType === FormQuestionTypeKeys.copay) {
    return {
      chargePatientBalance: false,
    };
  }
  if (questionType === FormQuestionTypeKeys.dropdown) {
    return {
      displayRadioButton: false,
    };
  }
  if (questionType === FormQuestionTypeKeys.allergy) {
    return {
      mappingField: 'allergies',
    };
  }

  if (questionType === FormQuestionTypeKeys.medicationList) {
    return {
      mappingField: 'medications',
    };
  }

  if (questionType === FormQuestionTypeKeys.jump) {
    return {
      script: `let jump
if (context.get('patientForm').answers['squiggly'] === 'foo') jump = 'jump1';
jump`,
      answers: [
        {
          ...getDefaultValuesAnswerObject(),
          label: 'jump1',
        },
      ] as NestedValue<TFormAnswerValues[]>,
    };
  }

  if (
    questionType === FormQuestionTypeKeys.familyHistory ||
    questionType === FormQuestionTypeKeys.immunizationHistory ||
    questionType === FormQuestionTypeKeys.surgicalHistory
  ) {
    const mappingFields = {
      [FormQuestionTypeKeys.familyHistory]: 'familyHistory',
      [FormQuestionTypeKeys.immunizationHistory]: 'immunizationHistory',
      [FormQuestionTypeKeys.surgicalHistory]: 'surgicalHistory',
    };
    return {
      [`${replace(questionType, '_h', 'H')}`]: [
        {
          id: uuid(),
        },
      ],
      mappingField: mappingFields[questionType],
    };
  }

  if (questionType === FormQuestionTypeKeys.socialHistory) {
    const socialHistoryItems = [
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), code: 'A', label: 'Annulled', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'C', label: 'Common law', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'D', label: 'Divorced', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'I', label: 'Interlocutory', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'L', label: 'Legally Separated', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'M', label: 'Married', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'P', label: 'Polygamous', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'S', label: 'Never married', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'T', label: 'Domestic partner', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'U', label: 'Unmarried', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: 'W', label: 'Widowed', disabled: true },
        ],
        id: 'maritalStatus',
        mappingField: 'maritalStatus',
        squiggly: 'patient.maritalStatus',
        text: 'Marital Status',
        title: 'Marital Status',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), label: 'I smoke every day', code: '449868002', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'I smoke some days', code: '428041000124106', disabled: true },
          {
            ...getDefaultValuesAnswerObject(),
            label: "I used to smoke, but I don't anymore",
            code: '8517006',
            disabled: true,
          },
          { ...getDefaultValuesAnswerObject(), label: 'I never smoked', code: '266919005', disabled: true },
          {
            ...getDefaultValuesAnswerObject(),
            label: "I don't know if I smoked or not",
            code: '266927001',
            disabled: true,
          },
          { ...getDefaultValuesAnswerObject(), label: "I'm a heavy smoker", code: '428071000124103', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: "I'm a light smoker", code: '428061000124105', disabled: true },
        ],
        id: 'howMuchSmoke',
        mappingField: 'howMuchSmoke',
        squiggly: 'patient.howMuchSmoke',
        text: 'How much do you smoke?',
        title: 'How much do you smoke?',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'alcoholicDrinks',
        mappingField: 'alcoholicDrinks',
        squiggly: 'patient.alcoholicDrinks',
        text: 'How many alcoholic drinks do you drink per day?',
        title: 'How many alcoholic drinks do you drink per day?',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), label: 'Yes', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'No', disabled: true },
        ],
        id: 'recreationalDrugs',
        mappingField: 'recreationalDrugs',
        squiggly: 'patient.recreationalDrugs',
        text: 'Do you use recreational drugs?',
        title: 'Do you use recreational drugs?',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'exerciseHours',
        mappingField: 'exerciseHours',
        squiggly: 'patient.exerciseHours',
        text: 'How many hours do you exercise per week?',
        title: 'How many hours do you exercise per week?',
        visibility: FormQuestionVisibilityKeys.default,
      },
    ];

    return {
      subQuestions: socialHistoryItems as NestedValue<TFormSubQuestionValues[]>,
      visibility: FormQuestionVisibilityKeys.default,
    };
  }

  if (questionType === FormQuestionTypeKeys.demographics) {
    const demographicItems = [
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'firstname',
        mappingField: 'firstname',
        mustExist: true,
        required: true,
        squiggly: 'patient.firstname',
        title: 'First Name',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'lastname',
        mappingField: 'lastname',
        mustExist: true,
        required: true,
        squiggly: 'patient.lastname',
        title: 'Last Name',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.shortText, formType),
        id: 'middlename',
        mappingField: 'middlename',
        squiggly: 'patient.middlename',
        title: 'Middle Name',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), label: 'II', code: 'II', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'III', code: 'III', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'IV', code: 'IV', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Jr.', code: 'Jr.', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Sr.', code: 'Sr.', disabled: true },
        ],
        id: 'suffix',
        mappingField: 'suffix',
        squiggly: 'patient.suffix',
        title: 'Suffix',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.date, formType),
        id: 'dateOfBirth',
        mappingField: 'dateOfBirth',
        mustExist: true,
        required: true,
        squiggly: 'patient.dob',
        title: 'Date of Birth',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.address, formType),
        id: 'currentAddress',
        mappingField: 'address',
        squiggly: 'patient.currentAddress',
        title: 'Current Address',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.address, formType),
        id: 'previousAddress',
        mappingField: 'previousAddress',
        squiggly: 'patient.previousAddress',
        title: 'Previous Address',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.phoneNumber, formType),
        id: 'phone',
        mappingField: 'contactPhone',
        mustExist: true,
        required: true,
        squiggly: 'patient.phone',
        title: 'Phone Number',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.email, formType),
        id: 'email',
        mappingField: 'contactEmail',
        mustExist: true,
        required: true,
        squiggly: 'patient.email',
        title: 'Email',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), code: '2106-3', label: 'White', disabled: true },
          { ...getDefaultValuesAnswerObject(), code: '2054-5', label: 'Black or African American', disabled: true },
          {
            ...getDefaultValuesAnswerObject(),
            code: '1002-5',
            label: 'American Indian or Alaska Native',
            disabled: true,
          },
          { ...getDefaultValuesAnswerObject(), code: '2028-9', label: 'Asian', disabled: true },
          {
            ...getDefaultValuesAnswerObject(),
            code: '2076-8',
            label: 'Native Hawaiian or Other Pacific Islander',
            disabled: true,
          },
          { ...getDefaultValuesAnswerObject(), code: '2131-1', label: 'Other Race', disabled: true },
        ],
        id: 'race',
        mappingField: 'demographicRace',
        mustExist: true,
        required: true,
        squiggly: 'patient.race',
        title: 'Race',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          {
            ...getDefaultValuesAnswerObject(),
            code: '2135-2',
            label: 'Hispanic or Latino',
            disabled: true,
          },
          {
            ...getDefaultValuesAnswerObject(),
            code: '2186-5',
            label: 'Not Hispanic or Latino',
            disabled: true,
          },
        ],
        id: 'ethnicityGroup',
        mappingField: 'demographicEthnicity',
        mustExist: true,
        required: true,
        squiggly: 'patient.ethnicityGroup',
        title: 'Ethnicity',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), label: 'Male', code: 'M', disabled: true },
          {
            ...getDefaultValuesAnswerObject(),
            label: 'Female',
            code: 'F',
            disabled: true,
          },
          {
            ...getDefaultValuesAnswerObject(),
            label: 'Unknown',
            code: 'UNK',
            disabled: true,
          },
        ],
        id: 'birthSex',
        mappingField: 'birthSex',
        mustExist: true,
        required: true,
        squiggly: 'patient.birthSex',
        title: 'Sex (Assigned at Birth)',
        visibility: FormQuestionVisibilityKeys.default,
      },
      {
        ...getDefaultValuesQuestionObject(FormQuestionTypeKeys.dropdown, formType),
        answers: [
          { ...getDefaultValuesAnswerObject(), label: 'English', code: 'en', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Spanish', code: 'es', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Arabic', code: 'ar', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Armenian', code: 'hy', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Bengali', code: 'bn', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Burmese', code: 'my', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Chinese', code: 'zh', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'French', code: 'fr', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'German', code: 'de', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Greek', code: 'gr', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Gujarati', code: 'gu', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Haitian Creole', code: 'ht', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Hindi', code: 'hi', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Italian', code: 'it', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Korean', code: 'ko', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Marathi', code: 'mr', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Polish', code: 'pl', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Portuguese', code: 'pt', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Punjabi', code: 'pa', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Russian', code: 'ru', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Urdu', code: 'ur', disabled: true },
          { ...getDefaultValuesAnswerObject(), label: 'Vietnamese', code: 'vi', disabled: true },
        ],
        id: 'language',
        mappingField: 'language',
        mustExist: true,
        required: true,
        squiggly: 'patient.language',
        title: 'Preferred Language',
        visibility: FormQuestionVisibilityKeys.default,
      },
    ];

    return {
      subQuestions: demographicItems as NestedValue<TFormSubQuestionValues[]>,
      visibility: FormQuestionVisibilityKeys.default,
    };
  }

  if (questionType === FormQuestionTypeKeys.scheduler) {
    return {
      schedulerFilters: {
        appointmentType: [],
        facility: [],
        provider: [],
      },
      schedulerAllowResourcesSearching: true,
      schedulerAnswerId: '',
      schedulerBookOnFormComplete: true,
      schedulerHideCalendar: false,
      schedulerLockTimeout: 15,
      schedulerRequired: false,
      schedulerShowFilters: false,
      schedulerShowZipcodeFilter: false,
      schedulerUseExternalResources: false,
      schedulerZipcodeMaxDistance: -1,
    };
  }

  return {};
};

const getAnswerDefaults = (questionType: FormQuestionTypeKeys, canSelectAnswers: boolean, formType: string) => {
  if (!canSelectAnswers) {
    return [];
  }
  if (questionType === FormQuestionTypeKeys.insuranceCapture) {
    return insuranceCaptureBranchingAnswers.map((branchAnswer) => ({
      ...getDefaultValuesAnswerObject(),
      label: branchAnswer.english,
      labelPortuguese: branchAnswer.portuguese,
      labelSpanish: branchAnswer.spanish,
    }));
  }
  return [getDefaultValuesAnswerObject()];
};

export const moveQuestion = (
  questions: Array<TFormQuestionValues | TFormSubQuestionValues>,
  fromIndex: number,
  toIndex: number,
) => {
  questions.splice(toIndex, 0, questions.splice(fromIndex, 1)[0]);
};

export const getDefaultValuesQuestionObject = (
  questionType: FormQuestionTypeKeys,
  formType: string,
): TFormQuestionValues | TFormSubQuestionValues => {
  const questionId = generateRandomIdWithPrefix('item');
  const questionConfig = getQuestionItemConfiguration(questionType);

  return {
    actionLabel: '',
    actionLabelPortuguese: '',
    actionLabelSpanish: '',
    actionLink: '',
    actionLinkPortuguese: '',
    actionLinkSpanish: '',
    addToAppointment: false,
    allowMultipleSelection: false,
    allowOtherChoice: false,
    allowResourcesSearching: true,
    answers: getAnswerDefaults(questionType, questionConfig.canSelectAnswers, formType),
    copay: '',
    description: '',
    descriptionPortuguese: '',
    descriptionSpanish: '',
    docusign: '',
    filterAvailabilityBasedOnZipcode: false,
    hideCalendar: false,
    id: questionId,
    includeNoneOfAboveChoice: false,
    isDefaultCurrentDate: false,
    mappingField: '',
    mustExist: false,
    persistAnswer: false,
    redirectType: (questionConfig.isFinal
      ? RedirectConfigurationTypeKeys.final
      : RedirectConfigurationTypeKeys.none) as string,
    redirectValue: '',
    queueSquigglyMapping: [],
    required: false,
    resourceType: questionType === 'resources' ? 'facility' : undefined,
    resourceFilterParams:
      questionType === 'resources' ? { facility: [], provider: [], appointmentType: [] } : undefined,
    schedulerAnswerId: '',
    schedulerRequired: false,
    scoringEnabled: false,
    script: '',
    showAnswerCodeField: false,
    showCustomAction: false,
    showDescription: questionConfig.showDescriptionField,
    showFilters: false,
    showZipcodeFilter: false,
    squiggly: questionId,
    subQuestions: [] as NestedValue<[]>,
    template: '',
    templatePortuguese: '',
    templateSpanish: '',
    text: '',
    title: questionConfig.titlePlaceholder || '',
    titlePortuguese: '',
    titleSpanish: '',
    type: questionType,
    useExternalResources: false,
    useFilterFromResourcesQuestions: false,
    visibility: FormQuestionVisibilityKeys.default,
    zipcodeMaxDistance: -1,
    ...getQuestionCustomInformation(questionType, formType),
  } as TFormQuestionValues;
};

const getAllRequiredQuestionsFlag = (questions: any) => {
  const questionsToCheck = getQuestionsToCheck(questions);
  const requiredQuestions = filter(questionsToCheck, { required: true });
  const optionalQuestions = filter(questionsToCheck, { required: false });

  if (!questionsToCheck.length || questionsToCheck.length === optionalQuestions.length) {
    return FormAllRequiredQuestionsKeys.none;
  }

  if (questionsToCheck.length && questionsToCheck.length === requiredQuestions.length) {
    return FormAllRequiredQuestionsKeys.all;
  }

  return FormAllRequiredQuestionsKeys.some;
};

const isInsuranceCapture = (question: TFormQuestionValues | TFormSubQuestionValues) =>
  get(question, 'type') === FormQuestionTypeKeys.insuranceCapture;
const includeInsuranceCardPlaceholder = (question: TFormQuestionValues | TFormSubQuestionValues) =>
  isInsuranceCapture(question) ? TInsuranceCardOptions.OPTIONAL : TInsuranceCardOptions.DEFAULT;

// GET FORM COMPONENTS INITIAL VALUES FUNCTIONS FOR EXISTING ITEMS --------------------------------------------
const formatQuestionsInitialData = (questions: any): TFormQuestionValues[] | TFormSubQuestionValues[] => {
  if (!questions) {
    return [];
  }
  return map(
    questions,
    (question: any): TFormQuestionValues => ({
      allowResourcesSearching: get(question, 'properties.allowResourcesSearching', true),
      actionLabel: get(question, 'properties.actionLabel', ''),
      actionLabelPortuguese: get(question, 'locales.pt.properties.actionLabel', ''),
      actionLabelSpanish: get(question, 'locales.es.properties.actionLabel', ''),
      actionLink: get(question, 'properties.actionHref', ''),
      actionLinkPortuguese: get(question, 'locales.pt.properties.actionHref', ''),
      actionLinkSpanish: get(question, 'locales.es.properties.actionHref', ''),
      addToAppointment: get(question, 'properties.addToAppointment', false),
      allowMultipleSelection: get(question, 'properties.allowMultipleSelection', false),
      allowOtherChoice: get(question, 'properties.allowOtherChoice', false),
      answers: getQuestionItemConfiguration(question.type).canSelectAnswers
        ? (formatAnswersInitialData(get(question, 'properties.answers', [])) as NestedValue<TFormAnswerValues[]>)
        : ([] as NestedValue<[]>),
      canSkip: get(question, 'properties.canSkip', true),
      copay: get(question, 'properties.copay', ''),
      chargePatientBalance: get(question, 'properties.chargePatientBalance', false),
      displayRadioButton: get(question, 'properties.displayRadioButton', false),
      docusign: get(question, 'properties.docusign_templates.id', ''),
      description: get(question, 'description', ''),
      descriptionPortuguese: get(question, 'locales.pt.description', ''),
      descriptionSpanish: get(question, 'locales.es.description', ''),
      familyHistory: formatHistoryInitialData(get(question, 'properties.familyHistory', [])) as NestedValue<
        THistoryQuestionValues[]
      >,
      filterAvailabilityBasedOnZipcode: get(question, 'properties.filterAvailabilityBasedOnZipcode', false),
      hideCalendar: get(question, 'properties.hideCalendar', false),
      id: get(question, '_id', get(question, 'id', '')),
      immunizationHistory: formatHistoryInitialData(get(question, 'properties.immunizationHistory', [])) as NestedValue<
        THistoryQuestionValues[]
      >,
      includeNoneOfAboveChoice: get(question, 'properties.includeNoneOfAboveChoice', false),
      includeInsuranceCard: get(question, 'properties.includeInsuranceCard', includeInsuranceCardPlaceholder(question)),
      insuranceCaptureMethod: get(question, 'properties.insuranceCaptureMethod', EInsuranceCaptureMethod.MANUAL),
      runInsuranceEligibility: get(question, 'properties.runInsuranceEligibility', false),
      onlyPullInsuranceEHR: get(question, 'properties.onlyPullInsuranceEHR', false),
      writebackPDFToEhrOnlyWhenInsuranceIsAltered: get(
        question,
        'properties.writebackPDFToEhrOnlyWhenInsuranceIsAltered',
        false,
      ),
      validationMessageRequired: get(question, 'validations.messages.required'),
      insuranceEligibilityFailed: get(question, 'validations.messages.insuranceEligibilityFailed'),
      insuranceEligibilityNotEligible: get(question, 'validations.messages.insuranceEligibilityNotEligible'),
      lumaPayCopayUnknown: get(question, 'validations.messages.lumaPayCopayUnknown'),
      promptInsuranceDeletion: get(question, 'properties.promptInsuranceDeletion'),
      promptInsuranceDeletionMessage: get(question, 'validations.messages.promptInsuranceDeletion'),
      isPatientSignature: get(question, 'properties.isPatientSignature'),
      showSignatureDisclaimer: get(question, 'properties.showSignatureDisclaimer', true),
      signatureDisclaimerText: get(question, 'properties.signatureDisclaimerText', ''),
      allowSaveSignature: get(question, 'properties.allowSaveSignature'),
      isDefaultCurrentDate: get(question, 'properties.isDefaultCurrentDate', false),
      mappingField: get(question, 'properties.mappingField', ''),
      mappingFieldIndicator: get(question, 'properties.mappingFieldIndicator', ''),
      mustExist: get(question, 'properties.mustExist', false),
      persistAnswer: get(question, 'properties.persistAnswer', false),
      // Now we save scheduler instead of reschedule, but we did not ran a migrate script.
      redirectType: replace(
        get(question, 'redirectType', RedirectConfigurationTypeKeys.none),
        'reschedule',
        RedirectConfigurationTypeKeys.scheduler,
      ) as string,
      redirectValue: getRedirectConfigurationValue(question),
      queueSquigglyMapping: get(question, 'properties.queueSquigglyMapping', []),
      required: get(question, 'validations.required', false),
      resourceType: question.type === 'resources' ? get(question, 'properties.resourceType', 'facility') : undefined,
      resourceFilterParams:
        question.type === 'resources'
          ? get(question, 'properties.resourceFilterParams', { facility: [], provider: [], appointmentType: [] })
          : undefined,
      schedulerAllowResourcesSearching: get(question, 'properties.scheduler.allowResourcesSearching', false),
      schedulerAnswerId: get(question, 'properties.scheduler.answerId', ''),
      schedulerBookOnFormComplete: get(question, 'properties.scheduler.bookOnFormComplete', true),
      schedulerFilters: get(question, 'properties.scheduler.filters', {}),
      schedulerHideCalendar: get(question, 'properties.scheduler.hideCalendar', false),
      schedulerLockTimeout: parseInt(get(question, 'properties.scheduler.lockTimeout', 15), 10),
      schedulerRequired: get(question, 'properties.scheduler.required', false),
      schedulerShowFilters: get(question, 'properties.scheduler.showFilters', false),
      schedulerShowZipcodeFilter: get(question, 'properties.scheduler.showZipcodeFilter', false),
      schedulerUseExternalResources: get(question, 'properties.scheduler.useExternalResources', false),
      schedulerZipcodeMaxDistance: parseInt(get(question, 'properties.scheduler.zipcodeMaxDistance', -1), 10),
      scoringEnabled: get(question, 'properties.scoringEnabled', false),
      script: get(question, 'properties.script', ''),
      showAnswerCodeField: get(question, 'properties.showAnswerCodeField', false),
      showCustomAction: get(question, 'properties.showCustomAction', false),
      showDescription: get(question, 'properties.showDescription', false),
      showFilters: get(question, 'properties.showFilters', false),
      showZipcodeFilter: get(question, 'properties.showZipcodeFilter', false),
      socialHistory: get(question, 'properties.socialHistory', undefined),
      squiggly: get(question, 'squiggly', ''),
      subQuestions: formatQuestionsInitialData(get(question, 'properties.items', [])) as NestedValue<
        TFormSubQuestionValues[]
      >,
      surgicalHistory: formatHistoryInitialData(get(question, 'properties.surgicalHistory', [])) as NestedValue<
        THistoryQuestionValues[]
      >,
      template: get(question, 'properties.template', ''),
      templatePortuguese: get(question, 'locales.pt.properties.template', ''),
      templateSpanish: get(question, 'locales.es.properties.template', ''),
      text: get(question, 'text', ''),
      title: get(question, 'title', ''),
      titlePortuguese: get(question, 'locales.pt.title', ''),
      titleSpanish: get(question, 'locales.es.title', ''),
      type: get(question, 'type', FormQuestionTypeKeys.multipleChoice),
      imhQuestionnaires: get(question, 'properties.imhQuestionnaires'),
      useExternalResources: get(question, 'properties.useExternalResources', false),
      useFilterFromResourcesQuestions: get(question, 'properties.useFilterFromResourcesQuestions', false),
      visibility: get(question, 'properties.visibility', FormQuestionVisibilityKeys.default),
      zipcodeMaxDistance: get(question, 'properties.zipcodeMaxDistance', -1),
      allergyReactionRequired: get(question, 'properties.allergy.reactionRequired'),
      allergySeverityRequired: get(question, 'properties.allergy.severityRequired'),
    }),
  );
};

const formatHistoryInitialData = (historyValues: any): THistoryQuestionValues[] =>
  map(historyValues, (value: any) => ({
    id: value.id,
    code: value.code || '',
    codeSystem: value.codeSystem || '',
    description: value.description || '',
    friendlyName: value.friendlyName || '',
    friendlyNamePortuguese: get(value, 'locales.pt.friendlyName', ''),
    friendlyNameSpanish: get(value, 'locales.es.friendlyName', ''),
  }));

const formatAnswersInitialData = (answers: any): TFormAnswerValues[] => {
  if (!answers) {
    return [];
  }

  return map(
    answers,
    (answer: any): TFormAnswerValues => ({
      allowResourcesSearching: get(answer, 'properties.allowResourcesSearching', true),
      code: get(answer, 'code', get(answer, 'label', '')),
      disabled: get(answer, 'disabled', false),
      hideCalendar: get(answer, 'properties.hideCalendar', false),
      id: get(answer, '_id', get(answer, 'id', '')),
      label: get(answer, 'label', ''),
      labelPortuguese: get(answer, 'locales.pt.label', ''),
      labelSpanish: get(answer, 'locales.es.label', ''),
      // Now we save scheduler instead of reschedule, but we did not ran a migrate script.
      redirectType: replace(
        get(answer, 'redirectType', RedirectConfigurationTypeKeys.none),
        'reschedule',
        RedirectConfigurationTypeKeys.scheduler,
      ) as string,
      redirectValue: getRedirectConfigurationValue(answer),
      queueSquigglyMapping: get(answer, 'properties.queueSquigglyMapping', []),
      score: get(answer, 'score', ''),
      showFilters: get(answer, 'properties.showFilters', false),
      showZipcodeFilter: get(answer, 'properties.showZipcodeFilter', false),
      useExternalResources: get(answer, 'properties.useExternalResources', false),
      useFilterFromResourcesQuestions: get(answer, 'properties.useFilterFromResourcesQuestions', false),
      zipcodeMaxDistance: get(answer, 'properties.zipcodeMaxDistance', -1),
    }),
  );
};

export const formatCustomFormsInitialData = (data?: TPatientFormTemplate): TCustomFormValues => {
  if (!data) {
    return getDefaultValuesCustomFormObject();
  }

  const questions = formatQuestionsInitialData(get(data, 'template.items', [])) as NestedValue<TFormQuestionValues[]>;
  const formData: TCustomFormValues = {
    active: get(data, 'active', false),
    allRequired: getAllRequiredQuestionsFlag(questions),
    checklistCategory: get(data, 'checklistCategory', EChecklistItemCategory.Intake),
    skipWelcomeQuestion: get(data, 'template.welcomeScreens[0].properties.skipWelcomeQuestion', ''),
    id: get(data, '_id', get(data, 'id', '')),
    insurance: get(data, 'insurance.enabled', false),
    language: Language.English,
    questions,
    jumpLogic: get(data, 'jumpLogic') as TCustomFormValues['jumpLogic'],
    questionsForStaffReview: get(data, 'questionsForStaffReview', {}),
    reminder: get(data, 'reminder.enabled', false),
    sendToDirectMessageEmail: get(data, 'sendToDirectMessageEmail', false),
    writebackToEhr: get(data, 'writebackToEhr', false),
    writebackPDFToEhr: get(data, 'writebackPDFToEhr', false),
    categoryNameToWritebackPDF: get(data, 'categoryNameToWritebackPDF', ''),
    respectTemplateHTML: get(data, 'respectTemplateHTML', false),
    source: get(data, 'externalId.source', FormSourceKeys.lumaHealth),
    templateAction: get(data, 'template.welcomeScreens[0].properties.actionLabel', ''),
    templateActionPortuguese: get(data, 'template.welcomeScreens[0].locales.pt.properties.actionLabel', ''),
    templateActionSpanish: get(data, 'template.welcomeScreens[0].locales.es.properties.actionLabel', ''),
    templateId: get(data, 'template.welcomeScreens[0].id', ''),
    templateText: get(data, 'template.welcomeScreens[0].properties.template', ''),
    templateTextPortuguese: get(data, 'template.welcomeScreens[0].locales.pt.properties.template', ''),
    templateTextSpanish: get(data, 'template.welcomeScreens[0].locales.es.properties.template', ''),
    templateTitle: get(data, 'template.welcomeScreens[0].title', ''),
    templateTitlePortuguese: get(data, 'template.welcomeScreens[0].locales.pt.title', ''),
    templateTitleSpanish: get(data, 'template.welcomeScreens[0].locales.es.title', ''),
    templateWrapLabels: get(data, 'template.wrapLabels', false),
    title: get(data, 'title', ''),
    type: get(data, 'type', FormTypeKeys.intake),
    version: get(data, 'template.version', ''),
    facilities: join(get(data, 'facilities'), ','),
  };

  return formData;
};

// CONVERT FORM DATA TO BACKEND DATA FORMAT FUNCTIONS ---------------------------------------------------------
const formatAnswerDataToSave = (values: TFormAnswerValues) => ({
  id: values.id,
  code: values.code || values.label || values.labelSpanish || values.labelPortuguese,
  disabled: values.disabled,
  redirectType: values.redirectType,
  label: values.label || values.labelSpanish || values.labelPortuguese,
  locales: {
    es: {
      label: values.labelSpanish,
    },
    pt: {
      label: values.labelPortuguese,
    },
  },
  score: values.score ? values.score : undefined,
  properties: {
    allowResourcesSearching: values.allowResourcesSearching,
    url: values.redirectType === RedirectConfigurationTypeKeys.url ? values.redirectValue : undefined,
    queue: values.redirectType === RedirectConfigurationTypeKeys.joinQueue ? values.redirectValue : undefined,
    queueSquigglyMapping:
      !isEmpty(values.queueSquigglyMapping) && values.redirectType === RedirectConfigurationTypeKeys.joinQueue
        ? values.queueSquigglyMapping
        : undefined,
    goTo: values.redirectType === RedirectConfigurationTypeKeys.goTo ? values.redirectValue : undefined,
    goToForm: values.redirectType === RedirectConfigurationTypeKeys.goToForm ? values.redirectValue : undefined,
    hideCalendar: values.hideCalendar,
    params: values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.redirectValue : undefined,
    showFilters: values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.showFilters : false,
    showZipcodeFilter:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.showZipcodeFilter : false,
    useExternalResources:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.useExternalResources : false,
    useFilterFromResourcesQuestions:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.useFilterFromResourcesQuestions : false,
    zipcodeMaxDistance: values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.zipcodeMaxDistance : 0,
  },
});

const formatHistoryDataToSave = (values: THistoryQuestionValues[]): any =>
  map(values, (value: THistoryQuestionValues) => {
    const historyData = {
      id: value.id,
      code: value.code,
      codeSystem: value.codeSystem,
      description: value.description,
      friendlyName: value.friendlyName,
    };

    if (value.friendlyNameSpanish) {
      set(historyData, 'locales.es.friendlyName', value.friendlyNameSpanish);
    }
    if (value.friendlyNamePortuguese) {
      set(historyData, 'locales.pt.friendlyName', value.friendlyNamePortuguese);
    }

    return historyData;
  });

const formatQuestionDataToSave = (
  values: TFormQuestionValues | TFormSubQuestionValues,
  docusignOptions: TList[],
  nested?: boolean,
): any => {
  const rawSubQuestions: TFormSubQuestionValues[] | [] = get(values, 'subQuestions', []);
  const properties = {
    resourceType: values.resourceType,
    resourceFilterParams: values.resourceFilterParams,
    allowResourcesSearching: values.allowResourcesSearching,
    actionHref: values.actionLink,
    actionLabel: values.actionLabel,
    addToAppointment: values.addToAppointment,
    allowMultipleSelection: values.allowMultipleSelection,
    allowOtherChoice: values.allowOtherChoice,
    answers: values.answers.length
      ? map(values.answers, (answer: TFormAnswerValues) => formatAnswerDataToSave(answer))
      : [],
    copay: values.copay,
    chargePatientBalance: values.chargePatientBalance,
    displayRadioButton: values.displayRadioButton,
    docusign_templates: find(docusignOptions, { id: values.docusign }),
    url: values.redirectType === RedirectConfigurationTypeKeys.url ? values.redirectValue : undefined,
    queue: values.redirectType === RedirectConfigurationTypeKeys.joinQueue ? values.redirectValue : undefined,
    queueSquigglyMapping:
      !isEmpty(values.queueSquigglyMapping) && values.redirectType === RedirectConfigurationTypeKeys.joinQueue
        ? values.queueSquigglyMapping
        : undefined,
    goTo: values.redirectType === RedirectConfigurationTypeKeys.goTo ? values.redirectValue : undefined,
    goToForm: values.redirectType === RedirectConfigurationTypeKeys.goToForm ? values.redirectValue : undefined,
    hideCalendar: values.hideCalendar,
    filterAvailabilityBasedOnZipcode: values.filterAvailabilityBasedOnZipcode,
    includeNoneOfAboveChoice: values.includeNoneOfAboveChoice,
    includeInsuranceCard: values.includeInsuranceCard,
    insuranceCaptureMethod: values.insuranceCaptureMethod,
    runInsuranceEligibility: values.runInsuranceEligibility,
    onlyPullInsuranceEHR: values.onlyPullInsuranceEHR,
    writebackPDFToEhrOnlyWhenInsuranceIsAltered: values.writebackPDFToEhrOnlyWhenInsuranceIsAltered,
    promptInsuranceDeletion: values.promptInsuranceDeletion,
    isPatientSignature: values.isPatientSignature,
    ...(values.isPatientSignature ? { allowSaveSignature: values.allowSaveSignature } : {}),
    showSignatureDisclaimer: values.showSignatureDisclaimer,
    ...(values.showSignatureDisclaimer ? { signatureDisclaimerText: values.signatureDisclaimerText } : {}),
    isDefaultCurrentDate: values.isDefaultCurrentDate,
    items: rawSubQuestions.length
      ? map(rawSubQuestions, (question: TFormSubQuestionValues) =>
          formatQuestionDataToSave(question, docusignOptions, true),
        )
      : [],
    mappingField: values.mappingField || '',
    ...(INDICATOR_FIELDS.includes(values.mappingField)
      ? {
          mappingFieldIndicator: values.mappingFieldIndicator,
        }
      : {}),
    mustExist: values.mustExist,
    params: values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.redirectValue : undefined,
    persistAnswer: values.persistAnswer,
    scoringEnabled: values.scoringEnabled,
    script: values.script,
    showAnswerCodeField: values.showAnswerCodeField,
    showCustomAction: values.showCustomAction,
    showDescription: values.showDescription,
    showFilters: values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.showFilters : false,
    showZipcodeFilter:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.showZipcodeFilter : false,
    useExternalResources:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.useExternalResources : false,
    useFilterFromResourcesQuestions:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.useFilterFromResourcesQuestions : false,
    template: values.template,
    visibility: values.visibility,
    zipcodeMaxDistance:
      values.redirectType === RedirectConfigurationTypeKeys.scheduler ? values.zipcodeMaxDistance : '',
    ...(values.allergyReactionRequired || values.allergySeverityRequired
      ? {
          allergy: {
            reactionRequired: values.allergyReactionRequired,
            severityRequired: values.allergySeverityRequired,
          },
        }
      : {}),
  };

  // subquestions has not ccda history questions or canSkip property
  if (!nested) {
    set(properties, 'scheduler.filters', get(values, 'schedulerFilters', {}));
    set(properties, 'scheduler.hideCalendar', get(values, 'schedulerHideCalendar', false));
    set(properties, 'scheduler.allowResourcesSearching', get(values, 'schedulerAllowResourcesSearching', false));
    set(properties, 'scheduler.showFilters', get(values, 'schedulerShowFilters', false));
    set(properties, 'scheduler.required', get(values, 'schedulerRequired', false));
    set(properties, 'scheduler.answerId', get(values, 'schedulerAnswerId', ''));
    set(properties, 'scheduler.showZipcodeFilter', get(values, 'schedulerShowZipcodeFilter', false));
    set(properties, 'scheduler.useExternalResources', get(values, 'schedulerUseExternalResources', false));
    set(properties, 'scheduler.zipcodeMaxDistance', parseInt(get(values, 'schedulerZipcodeMaxDistance', '-1'), 10));
    set(properties, 'scheduler.bookOnFormComplete', get(values, 'schedulerBookOnFormComplete', true));
    set(properties, 'scheduler.lockTimeout', parseInt(get(values, 'schedulerLockTimeout', '15'), 10));
    set(properties, 'canSkip', get(values, 'canSkip', true));
    set(properties, 'familyHistory', formatHistoryDataToSave(get(values, 'familyHistory', [])));
    set(properties, 'immunizationHistory', formatHistoryDataToSave(get(values, 'immunizationHistory', [])));
    set(properties, 'socialHistory', get(values, 'socialHistory'));
    set(properties, 'imhQuestionnaires', get(values, 'imhQuestionnaires'));
    set(properties, 'surgicalHistory', formatHistoryDataToSave(get(values, 'surgicalHistory', [])));
  }

  const data = {
    id: values.id,
    description: values.description,
    locales: {
      es: {
        description: values.descriptionSpanish,
        title: values.titleSpanish,
        properties: {
          actionHref: values.actionLinkSpanish,
          actionLabel: values.actionLabelSpanish,
          template: values.templateSpanish,
        },
      },
      pt: {
        description: values.descriptionPortuguese,
        title: values.titlePortuguese,
        properties: {
          actionHref: values.actionLinkPortuguese,
          actionLabel: values.actionLabelPortuguese,
          template: values.templatePortuguese,
        },
      },
    },
    redirectType: values.redirectType,
    squiggly: values.squiggly,
    text: values.text,
    title: values.title || values.titleSpanish || values.titlePortuguese,
    type: values.type,
    properties,
    validations: {
      required: values.required,
      messages: {
        required: values.validationMessageRequired,
        ...(values.runInsuranceEligibility
          ? {
              insuranceEligibilityFailed: values.insuranceEligibilityFailed,
              insuranceEligibilityNotEligible: values.insuranceEligibilityNotEligible,
            }
          : {}),
        ...(values.lumaPayCopayUnknown ? { lumaPayCopayUnknown: values.lumaPayCopayUnknown } : {}),
        ...(values.promptInsuranceDeletionMessage
          ? { promptInsuranceDeletion: values.promptInsuranceDeletionMessage }
          : {}),
      },
    },
  };

  return data;
};

export const formatCustomFormDataToSave = (values: TCustomFormValues, docusignOptions: TList[]) => {
  // @ts-expect-error BE is creating an _id for the jump and its causing an issue on the PUT request
  const { _id, ...jumpLogicData } = values.jumpLogic || {};
  const data: any = {
    active: values.active,
    title: values.title,
    type: values.type,
    sendToDirectMessageEmail: values.sendToDirectMessageEmail,
    writebackToEhr: values.writebackToEhr,
    writebackPDFToEhr: values.writebackPDFToEhr,
    categoryNameToWritebackPDF: values.categoryNameToWritebackPDF,
    checklistCategory: values.checklistCategory,
    jumpLogic: jumpLogicData,
    questionsForStaffReview: values.questionsForStaffReview || {},
    respectTemplateHTML: !!values.respectTemplateHTML,
    reminder: {
      enabled: values.reminder,
    },
    insurance: {
      enabled: values.insurance,
    },
    externalId: {
      source: values.source,
    },
    template: {
      version: values.version,
      items: map(values.questions, (question: TFormQuestionValues) =>
        formatQuestionDataToSave(question, docusignOptions),
      ),
      welcomeScreens: [
        {
          id: values.templateId,
          title: values.templateTitle,
          type: 'template_welcome',
          locales: {
            es: {
              title: values.templateTitleSpanish,
              properties: {
                actionLabel: values.templateActionSpanish,
                template: values.templateTextSpanish,
              },
            },
            pt: {
              title: values.templateTitlePortuguese,
              properties: {
                actionLabel: values.templateActionPortuguese,
                template: values.templateTextPortuguese,
              },
            },
          },
          properties: {
            actionLabel: values.templateAction,
            template: values.templateText,
            skipWelcomeQuestion: values.skipWelcomeQuestion,
          },
        },
      ],
      wrapLabels: values.templateWrapLabels,
    },
    facilities: compact(split(values.facilities, ',')),
  };

  if (values.id) {
    data._id = values.id;
  }

  return data;
};

// VALIDATION SCHEMA --------------------------------------------------------------------------------------------
const friendlyNameValidationSchema = {
  friendlyName: yup.string().when(['showFriendlyName', '$language'], {
    is: (showFriendlyName: boolean, language: string) => showFriendlyName && language === Language.English,
    then: yup.string().required('Required'),
  }),
  friendlyNamePortuguese: yup.string().when(['showFriendlyName', '$language'], {
    is: (showFriendlyName: boolean, language: string) => showFriendlyName && language === Language.Portuguese,
    then: yup.string().required('Required'),
  }),
  friendlyNameSpanish: yup.string().when(['showFriendlyName', '$language'], {
    is: (showFriendlyName: boolean, language: string) => showFriendlyName && language === Language.Spanish,
    then: yup.string().required('Required'),
  }),
};

const answersValidationSchema = {
  label: yup.string().required('Required'),
  labelPortuguese: yup.string().when('$language', {
    is: Language.Portuguese,
    then: yup.string().required('Required'),
  }),
  labelSpanish: yup.string().when('$language', {
    is: Language.Spanish,
    then: yup.string().required('Required'),
  }),
};

// adding this custom function cuz, for some reason, yup matches function is not working for this validation
// eslint-disable-next-line
yup.addMethod(yup.string, 'hasDangerousHtml', function(errorMessage) {
  // eslint-disable-next-line
  return this.test(`has-dangerous-html`, errorMessage, function(value) {
    const { path, createError } = this;

    // match <script> tag and tags with `on` attributes like onclick or onchange
    return /<((script|[^>]+\son)[^>]*)>/gim.test(value) ? createError({ path, message: errorMessage }) : true;
  });
});

const templateValidationSchema = {
  template: yup.string().when(['type', '$language'], {
    is: (type: FormQuestionTypeKeys, language: string) =>
      type === FormQuestionTypeKeys.template && language === Language.English,
    // @ts-ignore
    then: yup.string().hasDangerousHtml('Script tags and event attributtes not allowed'),
  }),
  templatePortuguese: yup.string().when(['type', '$language'], {
    is: (type: FormQuestionTypeKeys, language: string) =>
      type === FormQuestionTypeKeys.template && language === Language.Portuguese,
    // @ts-ignore
    then: yup.string().hasDangerousHtml('Script tags and event attributtes not allowed'),
  }),
  templateSpanish: yup.string().when(['type', '$language'], {
    is: (type: FormQuestionTypeKeys, language: string) =>
      type === FormQuestionTypeKeys.template && language === Language.Spanish,
    // @ts-ignore
    then: yup.string().hasDangerousHtml('Script tags and event attributtes not allowed'),
  }),
};

const urlRegex = new RegExp(
  /^(https?:\/\/)?(?!-)(([a-z\d-]*[a-z\d]\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.\\~+]*)*(\?[;#:,&a-z\-\d%_.'~+=!-{}/]*)?(\\#[a-z\d_-]*=&:,%)?$/i,
);

const customActionValidationSchema = {
  actionLink: yup.string().when(['showCustomAction', '$language', 'actionLabel'], {
    is: (showCustomAction: boolean, language: string, actionLabel: string) =>
      showCustomAction && !isEmpty(actionLabel) && language === Language.English,
    then: yup.string().matches(urlRegex, 'Invalid url format'),
  }),
  actionLinkPortuguese: yup.string().when(['showCustomAction', '$language', 'actionLabel'], {
    is: (showCustomAction: boolean, language: string, actionLabel: string) =>
      showCustomAction && !isEmpty(actionLabel) && language === Language.Portuguese,
    then: yup.string().matches(urlRegex, 'Invalid url format'),
  }),
  actionLinkSpanish: yup.string().when(['showCustomAction', '$language', 'actionLabel'], {
    is: (showCustomAction: boolean, language: string, actionLabel: string) =>
      showCustomAction && !isEmpty(actionLabel) && language === Language.Spanish,
    then: yup.string().matches(urlRegex, 'Invalid url format'),
  }),
};

const validationSchemaSubQuestion = yup.object().shape({
  ...customActionValidationSchema,
  ...templateValidationSchema,
  title: yup.string().when(['$version', '$templateWrapLabels', 'type'], {
    is: (version: any, templateWrapLabels: boolean, type: FormQuestionTypeKeys) =>
      version?.includes(PATIENT_FORM_VERSION_REDESIGN_POSTFIX) &&
      !templateWrapLabels &&
      type !== FormQuestionTypeKeys.twoFactor &&
      type !== FormQuestionTypeKeys.eSignature &&
      type !== FormQuestionTypeKeys.template,
    then: yup.string().max(50, 'Title must be at most 50 characters'),
  }),
  answers: yup.array().of(
    yup.object().shape({
      ...answersValidationSchema,
    }),
  ),
});

const validationSchemaQuestion = yup.object().shape({
  ...customActionValidationSchema,
  ...templateValidationSchema,
  answers: yup.array().of(
    yup.object().shape({
      ...answersValidationSchema,
    }),
  ),
  copay: yup.string().when('type', {
    is: FormQuestionTypeKeys.copay,
    then: yup.string().required('Required'),
  }),
  docusign: yup.string().when('type', {
    is: FormQuestionTypeKeys.docusign,
    then: yup.string().required('Required'),
  }),
  imhQuestionnaires: yup.array().of(
    yup.object().shape({
      code: yup.string().required('Required'),
    }),
  ),
  familyHistory: yup.array().of(
    yup.object().shape({
      description: yup.string().required('Required'),
      ...friendlyNameValidationSchema,
    }),
  ),
  immunizationHistory: yup.array().of(
    yup.object().shape({
      description: yup.string().required('Required'),
      ...friendlyNameValidationSchema,
    }),
  ),
  socialHistory: yup.array().of(
    yup.object().shape({
      answers: yup.array().of(
        yup.object().shape({
          ...answersValidationSchema,
        }),
      ),
    }),
  ),
  subQuestions: yup.array().of(validationSchemaSubQuestion),
  surgicalHistory: yup.array().of(
    yup.object().shape({
      description: yup.string().required('Required'),
      ...friendlyNameValidationSchema,
    }),
  ),
});

export const validationSchema = yup.object({
  title: yup.string().required('Required'),
  respectTemplateHTML: yup.boolean().default(false),
  templateText: yup.string().when('$language', {
    is: Language.English,
    // @ts-ignore
    then: yup.string().hasDangerousHtml('Script tags and event attributtes not allowed'),
  }),
  templateTextPortuguese: yup.string().when('$language', {
    is: Language.Portuguese,
    // @ts-ignore
    then: yup.string().hasDangerousHtml('Script tags and event attributtes not allowed'),
  }),
  templateTextSpanish: yup.string().when('$language', {
    is: Language.Spanish,
    // @ts-ignore
    then: yup.string().hasDangerousHtml('Script tags and event attributtes not allowed'),
  }),
  questions: yup.array().of(validationSchemaQuestion),
});
